import "./FirstMarketplaceScheduler.scss";
import { useMutation } from "@apollo/client";
import Button from "components/Button/Button";
import {
  startMarketplaceAccountFullSyncMutation,
  startMarketplaceAccountPartialSyncMutation,
  startMarketplaceInvoiceSyncMutation,
} from "pages/FirstMarketplace/mutations";
import { MarketplaceConfig, SettingTypes } from "TypesAndInterfaces";
import { displayError, displaySuccess } from "utils/displayNotification";
import calendar from "../../assets/images/icons/calendar.svg";
import { useContext, useState } from "react";
import Modal from "components/Modal/Modal";
import InvoicePrompter from "./components/InvoicePrompter";
import { FirstMarketplaceContext } from "components/FirstMarketplaceForm/FirstMarketplace.context";
import Cron, { HEADER } from "react-cron-generator";
import { HeaderKeyType } from "react-cron-generator/build/meta";

interface IProps {
  marketplaceConfig: MarketplaceConfig;
  marketplaceId: number;
  setMarketplaceConfig: Function;
  setIsEditPayoutScheduleModalOpened: Function;
  setEditPayoutScheduleModalNumber: Function;
  isInvoicePullInitialized: boolean;
  firstInvoiceCycleDate: Date;
  editPayoutScheduleModalNumber: number;
  isEditPayoutScheduleModalOpened: boolean;
  handleCronChange: (val: string, text: string) => void;
  handleEditPayoutScheduleModalClose: () => void;
}

const FirstMarketplaceScheduler = ({
  marketplaceConfig,
  marketplaceId,
  setMarketplaceConfig,
  setIsEditPayoutScheduleModalOpened,
  setEditPayoutScheduleModalNumber,
  isInvoicePullInitialized,
  firstInvoiceCycleDate,
  editPayoutScheduleModalNumber,
  isEditPayoutScheduleModalOpened,
  handleCronChange,
  handleEditPayoutScheduleModalClose
}: IProps) => {
  //#region hooks

  const { setNewValues } = useContext(FirstMarketplaceContext);

  const [invoiceSyncDisabled, setInvoiceSyncDisabled] = useState(false);
  const [fullSyncDisabled, setFullSyncDisabled] = useState(false);
  const [partialSyncDisabled, setPartialSyncDisabled] = useState(false);
  const [isInvoiceDateModalOpened, setIsInvoiceDateModalOpened] =
    useState(false);
  const [localInvoicePullInitialized, setLocalInvoicePullInitialized] =
    useState(isInvoicePullInitialized);

  const [localFirstInvoiceCycleDate, setLocalFirstInvoiceCycleDate] =
    useState<Date>(firstInvoiceCycleDate);
  const [resetModal, setResetModal] = useState(false);
  //#endregion

  //#region API CALLS
  const [startMarketplaceInvoiceSync] = useMutation<
    { startMarketplaceInvoiceSync: boolean },
    { marketplaceId: number; startDate?: Date }
  >(startMarketplaceInvoiceSyncMutation, {
    onCompleted: (data) => {
      setInvoiceSyncDisabled(false);
      if (data) {
        displaySuccess("Sync succeeded");
        setLocalInvoicePullInitialized(true);
      }
    },
    onError: (e) => {
      setInvoiceSyncDisabled(false);
      displayError(e.message);
    },
  });

  const [startMarketplaceAccountFullSync] = useMutation<
    { startMarketplaceAccountFullSync: boolean },
    { marketplaceId: number }
  >(startMarketplaceAccountFullSyncMutation, {
    onCompleted: (data) => {
      setFullSyncDisabled(false);
      data && displaySuccess("Sync succeeded");
    },
    onError: (e) => {
      setFullSyncDisabled(false);
      displayError(e.message);
    },
  });

  const [startMarketplaceAccountPartialSync] = useMutation<
    { startMarketplaceAccountPartialSync: boolean },
    { marketplaceId: number }
  >(startMarketplaceAccountPartialSyncMutation, {
    onCompleted: (data) => {
      setPartialSyncDisabled(false);
      data && displaySuccess("Sync succeeded");
    },
    onError: (e) => {
      setPartialSyncDisabled(false);
      displayError(e.message);
    },
  });
  //#endregion

  const handleEditPayoutScheduleModalOpen = (orderNumber: number) => {
    setIsEditPayoutScheduleModalOpened(true);
    setEditPayoutScheduleModalNumber(orderNumber);
  };

  const handleInvoiceSyncWithDateObject = (date: Date) => {
    if (date) {
      setIsInvoiceDateModalOpened(false);
      setInvoiceSyncDisabled(true);
      setLocalFirstInvoiceCycleDate(date);
      setIsEditPayoutScheduleModalOpened(true);

      startMarketplaceInvoiceSync({
        variables: { marketplaceId, startDate: date },
      });
    }
  };

  const handleInvoiceSync = () => {
    setInvoiceSyncDisabled(true);
    if (localInvoicePullInitialized) {
      startMarketplaceInvoiceSync({
        variables: { marketplaceId },
      });
    } else {
      setIsInvoiceDateModalOpened(true);
    }
  };

  const handleFullSync = () => {
    setFullSyncDisabled(true);
    startMarketplaceAccountFullSync({
      variables: { marketplaceId },
    });
  };

  const handlePartialSync = () => {
    setPartialSyncDisabled(true);
    startMarketplaceAccountPartialSync({
      variables: { marketplaceId },
    });
  };

  const handleModalClose = (value: boolean) => {
    setIsInvoiceDateModalOpened(value);
    setInvoiceSyncDisabled(value);
    setResetModal(!resetModal);
  };

  const handleResetScheduler = (schedulerType: string) => {
    // Update marketplaceConfig
    setMarketplaceConfig((prev: MarketplaceConfig) => ({
      ...prev,
      account: {
        ...prev.account,
        [schedulerType]: {
          description: "",
          value: "",
        },
      },
    }));

    setNewValues((prevNewValues) => {
      const updatedValues = [...prevNewValues];
      const index = updatedValues.findIndex(
        (value) => value.property === schedulerType
      );

      const newValue = {
        property: schedulerType,
        value: { description: "", value: "" },
        type: SettingTypes.Account,
      };

      if (index !== -1) {
        updatedValues[index] = newValue;
      } else {
        updatedValues.push(newValue);
      }

      return updatedValues;
    });
  };

  const getCronJobOptions = () => {
    return {
      headers: [
        HEADER.MONTHLY,
        HEADER.WEEKLY,
        (editPayoutScheduleModalNumber !== 2 && HEADER.MINUTES),
        HEADER.HOURLY,
        HEADER.DAILY,
      ].filter((header) => header) as HeaderKeyType[],
    };
  };

  const handleIsEditPayoutScheduleModalOpened = (isOpened: boolean) => {
    setIsEditPayoutScheduleModalOpened(isOpened);
  };

  const cronJobTitle = () => {
    switch (editPayoutScheduleModalNumber) {
      case 1:
        return "Payout Schedule";
      case 2:
        return "Account Full Sync Schedule";
      case 3:
        return "Account Partial Sync Schedule";
      default:
        return "";
    }
  };

  return (
    <aside className="main-content__sidebar">
      <div className="schedule-wrapper">
        <div className="schedule-container">
          <h3>Payout schedule</h3>
          <div className="schedule-container__details">
            <div className="schedule-container__date">
              <img src={calendar} alt="" />
              <p>
                {marketplaceConfig.account.invoiceSyncScheduler?.description}
                {marketplaceConfig.account.invoiceSyncScheduler?.description && <br />}
                {localInvoicePullInitialized ?
                  `Start Date: ${new Date(localFirstInvoiceCycleDate).toLocaleDateString()}` :
                  <span className="schedule-container__invalid">Invoice date not set!</span>
                }
              </p>
            </div>
            <span>
              <Button
                className="button--grey"
                type="button"
                content={undefined}
                icon="pencil"
                onClick={() => handleEditPayoutScheduleModalOpen(1)}
              />
              {marketplaceConfig.account.invoiceSyncScheduler?.value && (
                <Button
                  className="button--grey"
                  type="button"
                  content={undefined}
                  icon="trash"
                  onClick={() => handleResetScheduler("invoiceSyncScheduler")}
                />
              )}
            </span>
          </div>
          <Button
            className="button--bordered button--green button--no-padding"
            type="button"
            content="Start Invoice Sync"
            icon="sync"
            onClick={handleInvoiceSync}
            disabled={invoiceSyncDisabled}
          ></Button>
        </div>

        <div className="schedule-container">
          <h3>Account full sync schedule</h3>
          <div className="schedule-container__details">
            <div className="schedule-container__date">
              <img src={calendar} alt="" />
              <p>
                {
                  marketplaceConfig.account.accountFullSyncScheduler
                    ?.description
                }
              </p>
            </div>
            <span>
              <Button
                className="button--grey"
                type="button"
                content={undefined}
                icon="pencil"
                onClick={() => handleEditPayoutScheduleModalOpen(2)}
              />
              {marketplaceConfig.account.accountFullSyncScheduler?.value && (
                <Button
                  className="button--grey"
                  type="button"
                  content={undefined}
                  icon="trash"
                  onClick={() => handleResetScheduler("accountFullSyncScheduler")}
                />
              )}
            </span>
          </div>
          <Button
            className="button--bordered button--green button--no-padding"
            type="button"
            content="Start Full Sync"
            icon="sync"
            onClick={handleFullSync}
            disabled={fullSyncDisabled}
          ></Button>
        </div>

        <div className="schedule-container">
          <h3>Account partial sync schedule</h3>
          <div className="schedule-container__details">
            <div className="schedule-container__date">
              <img src={calendar} alt="" />
              <p>
                {
                  marketplaceConfig.account.accountPartialSyncScheduler
                    ?.description
                }
              </p>
            </div>
            <span>
              <Button
                className="button--grey"
                type="button"
                content={undefined}
                icon="pencil"
                onClick={() => handleEditPayoutScheduleModalOpen(3)}
              />
              {marketplaceConfig.account.accountPartialSyncScheduler?.value && (
                <Button
                  className="button--grey"
                  type="button"
                  content={undefined}
                  icon="trash"
                  onClick={() => handleResetScheduler("accountPartialSyncScheduler")}
                />
              )}
            </span>
          </div>
          <Button
            className="button--bordered button--green button--no-padding"
            type="button"
            content="Start Partial Sync"
            icon="sync"
            onClick={handlePartialSync}
            disabled={partialSyncDisabled}
          ></Button>
        </div>
      </div>
      <Modal
        isOpened={isInvoiceDateModalOpened}
        title={`Input Invoice Date`}
        setIsOpenedCallback={handleModalClose}
      >
        <InvoicePrompter
          callback={handleInvoiceSyncWithDateObject}
          reset={resetModal}
        />
      </Modal>
      <Modal
        isOpened={isEditPayoutScheduleModalOpened}
        className="edit-payout-schedule"
        title={`Edit ${cronJobTitle()}`}
        setIsOpenedCallback={handleIsEditPayoutScheduleModalOpened}
      > {isInvoiceDateModalOpened ?
        <InvoicePrompter
          callback={handleInvoiceSyncWithDateObject}
          reset={resetModal}
        /> :
        <Cron
          onChange={handleCronChange}
          value={undefined}
          showResultText={true}
          showResultCron={true}
          options={getCronJobOptions()}
        />}
        <div className="modal-buttons-container">
          <Button
            className="button--bordered button--error"
            type="button"
            content="Cancel"
            icon=""
            onClick={handleEditPayoutScheduleModalClose}
          ></Button>
          <Button
            className="button--primary"
            type="button"
            content="OK"
            icon=""
            onClick={() => {
              setIsEditPayoutScheduleModalOpened(false);
            }}
          ></Button>
        </div>
      </Modal>
    </aside>
  );
};

export default FirstMarketplaceScheduler;
